/**
 * The Supabase auth storage adapter for the Android build (M2, brief §2.5).
 *
 * On native the session lives in Keystore-backed secure storage
 * (`platform.storage.secure`), never in WebView `localStorage`. On the web
 * this module is not used at all: `client.ts` keeps `localStorage`, so the
 * web path is byte-for-byte what it was.
 *
 * Every call first awaits one boot step, which runs before auth-js reads
 * anything:
 *   1. Migration from M1. M1 builds kept auth-js's state in `localStorage`,
 *      and it is more than the session: the PKCE verifier of a link
 *      requested but not yet tapped (`${key}-code-verifier`) and, with a
 *      separate user store, `${key}-user`. Every key with the auth prefix is
 *      moved to secure storage, then removed from `localStorage`. Without
 *      this every M1 install is signed out on upgrade, or keeps its token in
 *      `localStorage`.
 *   2. The idle deadline, enforced on a cold start (Codex on #1085, round 6).
 *      An app killed in the background and opened after its deadline never
 *      sees a resume. If the persisted deadline has passed, the auth keys are
 *      wiped here, so the client starts signed out and no signed-in route
 *      paints.
 *
 * A wipe must not report success while the session is still on disk (Codex
 * on #1093). The Keystore can refuse a removal, so every wipe verifies its
 * keys are gone. A key that survives is recorded as stale (in prefs, which
 * is a different store): from then on this adapter returns `null` for it,
 * so auth-js cannot load the ended session again in this run, and every boot
 * retries the removal. A later successful write of that key (a new sign-in)
 * replaces the stale value and clears the mark.
 *
 * `platform` is imported lazily: `client.ts` imports this module, and the web
 * platform imports the client, so a static import would be a cycle. The
 * dynamic import only ever runs in the native bundle.
 */
import type { KeyValueStore } from '@/platform/types';
import {
  IDLE_DEADLINE_PREF,
  NATIVE_SESSION_PREFS,
  STALE_AUTH_KEYS_PREF,
  WIPE_PENDING_DEADLINE,
  authKeysFor,
} from '@/lib/nativeSessionKeys';
import { logger } from '@/lib/logger';
import { underSessionStoreLock } from '@/lib/sessionStoreLock';
import { NewerSessionError, currentAuthReset } from '@/lib/authResetBinding';

export interface NativeAuthStorageDeps {
  stores: () => Promise<{ secure: KeyValueStore; prefs: KeyValueStore }>;
  local: Pick<Storage, 'length' | 'key' | 'getItem' | 'removeItem'>;
  now: () => number;
}

/** What supabase-js accepts as `auth.storage`: async get/set/remove. */
export interface AsyncAuthStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

// Built only when the adapter is created, which happens only in the native
// bundle, where `localStorage` always exists.
function defaultDeps(): NativeAuthStorageDeps {
  return {
    stores: async () => {
      const { platform } = await import('@/platform');
      return { secure: platform.storage.secure, prefs: platform.storage.prefs };
    },
    local: localStorage,
    now: () => Date.now(),
  };
}

// Auth keys whose removal failed. Module state, because the wipe
// (`localFirstWipe`) and the client's adapter must agree on it in one run.
const staleKeys = new Set<string>();
// Auth keys hidden for this run only, because the marker could not be read
// at boot. Nothing is deleted on that basis: an unreadable marker is not
// proof of an ended session, so the session is withheld, not wiped, and a
// later launch that can read the marker decides.
const hiddenThisRun = new Set<string>();

/** Resolves true when the marker is durably recorded (or durably cleared). */
async function persistStaleKeys(prefs: KeyValueStore): Promise<boolean> {
  try {
    if (staleKeys.size === 0) await prefs.remove(STALE_AUTH_KEYS_PREF);
    else await prefs.set(STALE_AUTH_KEYS_PREF, JSON.stringify([...staleKeys]));
    return true;
  } catch (e) {
    logger.warn('Could not record which auth keys are still to be removed', e);
    return false;
  }
}

/**
 * Reads the stale-key marker. Resolves `null` when its state is UNKNOWN (the
 * read failed twice, or the value is corrupt): the caller must then fail
 * closed rather than treat the marker as empty (Codex on #1093).
 */
async function loadStaleKeys(prefs: KeyValueStore): Promise<string[] | null> {
  for (let attempt = 0; attempt < 2; attempt++) {
    let raw: string | null;
    try {
      raw = await prefs.get(STALE_AUTH_KEYS_PREF);
    } catch (e) {
      logger.warn('Could not read which auth keys are still to be removed', e);
      continue;
    }
    try {
      const parsed: unknown = JSON.parse(raw ?? '[]');
      if (Array.isArray(parsed) && parsed.every((k) => typeof k === 'string')) return parsed;
    } catch {
      // Corrupt: unknown.
    }
    return null;
  }
  return null;
}

async function isGone(secure: KeyValueStore, key: string): Promise<boolean> {
  try {
    return (await secure.get(key)) === null;
  } catch {
    return false;
  }
}

/** Overwrites a key that can be neither removed nor recorded as stale. */
const WIPED = 'kanfy:wiped';

/**
 * Thrown when an auth key survives a wipe and nothing durable records that it
 * must not be used: not the removal, not the stale marker, not an overwrite.
 * Storage is failing across the board; the caller must not report a normal
 * sign-out (Codex on #1093).
 */
export class AuthWipeIncompleteError extends Error {
  constructor(readonly keys: string[]) {
    super('The session could not be removed from this device');
    this.name = 'AuthWipeIncompleteError';
  }
}

/**
 * Removes auth keys from secure storage and VERIFIES they are gone.
 *
 * The keys are marked stale in prefs BEFORE the removal, so a crash or a
 * failed removal part-way leaves them hidden and retried at every boot.
 * Resolves `'gone'` when every key is really gone, and `'hidden'` when one
 * survived but is durably unusable (the marker was recorded, or its value was
 * overwritten). Throws AuthWipeIncompleteError when neither holds.
 */
export async function removeAuthKeys(
  keys: string[],
  secure: KeyValueStore,
  prefs: KeyValueStore,
): Promise<'gone' | 'hidden'> {
  keys.forEach((k) => staleKeys.add(k));
  const markedBefore = await persistStaleKeys(prefs);
  await Promise.allSettled(keys.map((k) => secure.remove(k)));
  const left: string[] = [];
  for (const k of keys) {
    if (await isGone(secure, k)) staleKeys.delete(k);
    else left.push(k);
  }
  const markedAfter = await persistStaleKeys(prefs);
  if (!markedAfter) {
    // The marker on disk may still list the removed keys: this call's own
    // write, or an earlier one this call's failed write could not replace.
    // Keep them in memory until it is cleared, so a later sign-in's write
    // retries clearing it; otherwise the next launch would remove that new
    // session (Codex on #1093). They are gone, so hiding them costs nothing.
    keys.forEach((k) => staleKeys.add(k));
  }
  if (left.length === 0) return 'gone';

  logger.warn('Secure storage kept an auth key after a wipe; it is hidden and will be retried');
  if (markedBefore || markedAfter) return 'hidden';
  // Neither store records it: make the value itself unusable instead.
  const overwritten = await Promise.all(
    left.map(async (k) => {
      try {
        await secure.set(k, WIPED);
        return (await secure.get(k)) === WIPED;
      } catch {
        return false;
      }
    }),
  );
  if (overwritten.every(Boolean)) return 'hidden';
  throw new AuthWipeIncompleteError(left);
}

// Set by an incomplete wipe in this run: the idle deadline on disk is then
// the last durable guard, whatever its value, and the sign-out cleanup leaves
// it alone. A new session durably stored ends it (Codex on #1093): from then
// on the deadline belongs to that session.
let incompleteWipe = false;
export const incompleteWipeGuard = {
  mark(): void {
    incompleteWipe = true;
  },
  clear(): void {
    incompleteWipe = false;
  },
  active: (): boolean => incompleteWipe,
};

/**
 * The session value auth-js last read or wrote through this adapter, by key:
 * what a local wipe goes by when the store cannot be read at its start, so it
 * can still tell a refresh of the ended session from a new sign-in (Codex on
 * #1136). Unknown (undefined) until the first read or write of this run.
 */
const lastSeen = new Map<string, string | null>();
export function lastSeenAuthValue(key: string): string | null | undefined {
  return lastSeen.get(key);
}

/** Test seam: the stale-key set is module state. */
export function resetStaleAuthKeysForTests() {
  staleKeys.clear();
  hiddenThisRun.clear();
  lastSeen.clear();
  incompleteWipe = false;
}

async function migrateFromLocalStorage(authKey: string, secure: KeyValueStore, local: NativeAuthStorageDeps['local']) {
  for (const k of localAuthKeys(authKey, local)) {
    const value = local.getItem(k);
    if (value === null) continue;
    // A value already in secure storage was written by M2, so it is newer than
    // any M1 copy: a copy left by an earlier failed move must never overwrite
    // it, or a later sign-in could be replaced by the old account (Codex on
    // #1093). Unknown: leave both, and retry next launch.
    let existing: string | null;
    try {
      existing = await secure.get(k);
    } catch {
      continue;
    }
    if (existing !== null) {
      local.removeItem(k);
      continue;
    }
    try {
      await secure.set(k, value);
    } catch (e) {
      // Leave it in localStorage: a failed move must not lose the session.
      // The next launch tries again.
      logger.warn('Could not move an auth key into secure storage; will retry next launch', e);
      continue;
    }
    local.removeItem(k);
  }
}

function localAuthKeys(authKey: string, local: NativeAuthStorageDeps['local']): string[] {
  const keys: string[] = [];
  for (let i = 0; i < local.length; i++) {
    const k = local.key(i);
    if (k && k.startsWith(authKey)) keys.push(k);
  }
  return keys;
}

async function enforceBootDeadline(
  authKey: string,
  secure: KeyValueStore,
  prefs: KeyValueStore,
  local: NativeAuthStorageDeps['local'],
  now: number,
) {
  let raw: string | null | undefined;
  for (let attempt = 0; attempt < 2 && raw === undefined; attempt++) {
    try {
      raw = await prefs.get(IDLE_DEADLINE_PREF);
    } catch (e) {
      logger.warn('Could not read the idle deadline at boot', e);
    }
  }
  if (raw === undefined) {
    // Unknown is not "not expired" (Codex on #1093): fail closed. This run
    // starts signed out; nothing is deleted, so a later launch that can read
    // the deadline decides.
    authKeysFor(authKey).forEach((k) => hiddenThisRun.add(k));
    return;
  }
  if (raw === null) return;
  const deadline = Number(raw);
  if (!Number.isFinite(deadline) || deadline > now) return;
  // An M1 copy the migration could not move is expired too: delete it, or a
  // later launch would migrate it back with no deadline left to reject it
  // (Codex on #1093).
  localAuthKeys(authKey, local).forEach((k) => local.removeItem(k));
  try {
    // Keys that survive are marked stale, so the session stays unreachable
    // even though its deadline pref goes.
    await removeAuthKeys(authKeysFor(authKey), secure, prefs);
  } catch (e) {
    // Nothing durable records the wipe: keep the expired deadline so the
    // next boot enforces it again. This run stays signed out (stale keys).
    logger.error('Could not remove an expired session at boot', e);
    return;
  }
  await Promise.allSettled(NATIVE_SESSION_PREFS.filter((k) => k !== IDLE_DEADLINE_PREF).map((k) => prefs.remove(k)));
  // The expired deadline must not outlive this wipe: a sign-in later in this
  // run would read it and end at once (Codex on #1093). If it cannot be
  // removed, make it the wipe fallback, which a new session's write clears.
  try {
    await prefs.remove(IDLE_DEADLINE_PREF);
  } catch {
    await prefs.set(IDLE_DEADLINE_PREF, WIPE_PENDING_DEADLINE).catch(() => {});
  }
}

/**
 * `authKeyOf` may be a promise: the client module that defines the key
 * imports this adapter's module chain, so the key is read at boot, after
 * every module has evaluated.
 */
export function createNativeAuthStorage(
  authKeyOf: string | (() => Promise<string>),
  deps: NativeAuthStorageDeps = defaultDeps(),
): AsyncAuthStorage {
  let authKey = typeof authKeyOf === 'string' ? authKeyOf : '';
  let ready: Promise<{ secure: KeyValueStore; prefs: KeyValueStore }> | null = null;
  const boot = () =>
    (ready ??= (async () => {
      if (typeof authKeyOf !== 'string') authKey = await authKeyOf();
      const { secure, prefs } = await deps.stores();
      await migrateFromLocalStorage(authKey, secure, deps.local);
      const marker = await loadStaleKeys(prefs);
      if (marker === null) {
        // Unknown: fail closed. This run starts signed out; nothing is deleted.
        authKeysFor(authKey).forEach((k) => hiddenThisRun.add(k));
      } else if (marker.length > 0) {
        marker.forEach((k) => staleKeys.add(k));
        // A failure here keeps them stale (hidden) for this run.
        await removeAuthKeys(marker, secure, prefs).catch((e) =>
          logger.error('Could not remove a stale auth key at boot', e),
        );
      }
      await enforceBootDeadline(authKey, secure, prefs, deps.local, deps.now());
      return { secure, prefs };
    })());

  return {
    async getItem(key) {
      const { secure } = await boot();
      // An ended session whose removal failed is never handed back.
      if (staleKeys.has(key) || hiddenThisRun.has(key)) return null;
      const raw = await secure.get(key);
      const value = raw === WIPED ? null : raw;
      // The reset after a local wipe never sees a session but the ended one
      // (Codex on #1136): see authResetBinding.ts.
      const reset = currentAuthReset();
      if (reset && key === authKey && !reset.isEnded(key, value)) throw new NewerSessionError();
      if (key === authKey) lastSeen.set(key, value);
      return value;
    },
    // Under the session store lock (Codex on #1134): a sign-in's write never
    // lands between a session wipe's last check and its clear.
    setItem: (key, value) => underSessionStoreLock(async () => {
      const { secure, prefs } = await boot();
      await secure.set(key, value);
      if (key === authKey) lastSeen.set(key, value);
      // Any M1 copy of this key is older: it must not migrate over this value
      // at the next launch (Codex on #1093).
      try {
        deps.local.removeItem(key);
      } catch {
        // The migration's own check keeps the secure value anyway.
      }
      // The marker must be updated durably for the new value to survive the
      // next boot. If it cannot be, fail this write now rather than let a
      // session work for this run and vanish at the next launch (Codex on
      // #1093): the key goes back under protection and auth-js sees the error.
      const markerCleared = async (wasHidden: boolean) => {
        if (await persistStaleKeys(prefs)) return;
        if (wasHidden) hiddenThisRun.add(key);
        else staleKeys.add(key);
        throw new Error('Could not record the new session safely on this device');
      };
      if (hiddenThisRun.delete(key)) {
        // The marker on disk is unknown (unreadable or corrupt) and may list
        // this key, which now holds a new session: replace it with what this
        // run knows. The keys still hidden become stale, so the old values
        // stay unreachable and are removed at the next boot; the new key is
        // not listed, so that boot keeps it (Codex on #1093).
        staleKeys.delete(key);
        hiddenThisRun.forEach((k) => staleKeys.add(k));
        hiddenThisRun.clear();
        await markerCleared(true);
      } else if (staleKeys.delete(key)) {
        await markerCleared(false);
      }
      if (key === authKey) {
        // A new session is stored and its key protected durably: an earlier
        // incomplete wipe's fallback deadline is obsolete, and left in place
        // it would end this session at once.
        try {
          if ((await prefs.get(IDLE_DEADLINE_PREF)) === WIPE_PENDING_DEADLINE) await prefs.remove(IDLE_DEADLINE_PREF);
        } catch {
          // The idle logout then treats it as expired: fail closed.
        }
        incompleteWipe = false;
      }
    }),
    async removeItem(key) {
      const { secure, prefs } = await boot();
      // The reset after a local wipe removes only what the ended session
      // left; a key a sign-in wrote since is kept (Codex on #1136). One that
      // cannot be read is kept too: it may be that sign-in's. The check and
      // the removal run under the session store lock, so a sign-in's write
      // never lands between them (Codex on #1137).
      const reset = currentAuthReset();
      if (reset) {
        await underSessionStoreLock(async () => {
          const current = await secure.get(key).then((v) => (v === WIPED ? null : v), () => undefined);
          if (current === undefined || !reset.isEnded(key, current)) return;
          await removeAuthKeys([key], secure, prefs);
        });
        return;
      }
      // auth-js treats a removal as done, so a failed one must still leave
      // the key unreachable: removeAuthKeys marks it stale, and throws only
      // when nothing durable can record that.
      await removeAuthKeys([key], secure, prefs);
    },
  };
}
